import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import Form from 'react-bootstrap/Form';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { Container, Row, Col, Card } from 'reactstrap';
import * as ROUTES from '../../constants/routes';
import { clearImpersonatedClientId } from '@/utilities/impersonation';
import FooterComponent from '../Footer';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { UserContext } from '../App';
import { fetchSsoByEmail, startSsoLogin } from '../../utilities/ssoClient';

const IDENTIFY = 'identify';
const PASSWORD = 'password';

function Auth() {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [step, setStep] = useState(IDENTIFY);

    const passwordRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useContext(UserContext);

    useEffect(() => {
        if (user) {
            if (user?.roles?.['SHIPPER']) {
                navigate(`/shipper/oms/myorders`);
            } else if (user?.roles?.['CARRIER'] || user?.roles?.['CONTRACTOR']) {
                navigate(`/carrier/oms/myorders`);
            } else {
                navigate('/account');
            }
        }
    }, [user]);

    useEffect(() => {
        if (step === PASSWORD) {
            passwordRef.current?.focus();
        }
    }, [step]);

    const authenticate = async () => {
        await signInWithEmailAndPassword(getAuth(), email.toLowerCase(), password);
        // Fresh interactive sign-in — ensure no stale impersonation carries over.
        clearImpersonatedClientId();
        window.analytics.identify({ email: email });
        setEmail('');
        setPassword('');
    };

    // A domain with no SSO row falls through to the password field, and so does a lookup that could
    // not complete: fetchSsoByEmail answers null for both. Which one it was is the server's to know
    // -- offering the password field either way is what keeps this page from reporting who we have.
    const continueWithEmail = async () => {
        const sso = await fetchSsoByEmail(email);

        if (!sso) {
            setStep(PASSWORD);

            return;
        }

        try {
            await startSsoLogin(sso);
        } catch (err) {
            console.error(err);
            setErrors([err.message || 'Could not start SSO sign-in.']);
        }
    };

    const signInWithPassword = async () => {
        try {
            await authenticate();
        } catch (err) {
            setErrors(['Authentication ' + err.message.split(' ')[1].trim() + ': ' + err.message.split(' ')[2].trim()]);
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();

        setErrors([]);
        setLoading(true);

        if (step === IDENTIFY) {
            await continueWithEmail();
        } else {
            await signInWithPassword();
        }

        setLoading(false);
    };

    const bannerCutoff = Date.UTC(2025, 5, 13, 8, 0, 0, 0);
    const showBanner = Date.now() < bannerCutoff;

    return (
        <>
            <Container className="mt-5">
                <Row>
                    <Col className="d-flex justify-content-center mt-4">
                        <Card className="shadow border-0 signupform mt-5">
                            <Form onSubmit={onSubmit} style={{ margin: '5rem' }}>
                                {showBanner && (
                                    <Alert
                                        variant="danger"
                                        style={{ fontWeight: 600, textAlign: 'center', width: '500px' }}
                                    >
                                        Our authentication and other services are currently experiencing an outage due
                                        to issues with Google Cloud Platform. Some features may not work until Google
                                        resolves the issue. We appreciate your patience.
                                    </Alert>
                                )}
                                <Container className="d-flex justify-content-center mb-3">
                                    <h4>Sign in to your Onward account</h4>
                                </Container>

                                <Form.Control
                                    name="email"
                                    type="text"
                                    autoComplete="username"
                                    placeholder="Email Address"
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        // A different address is a different tenant until the lookup
                                        // says otherwise, so the password it was paired with is moot.
                                        setStep(IDENTIFY);
                                    }}
                                    value={email}
                                    style={{ marginBottom: '10px' }}
                                />

                                {/* Mounted from the first render so a password manager finds the pair; the
                                    step only decides whether it is on screen. */}
                                <div
                                    css={css`
                                        display: ${step === PASSWORD ? 'block' : 'none'};
                                    `}
                                >
                                    <Form.Control
                                        ref={passwordRef}
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="Password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        value={password}
                                        style={{ marginBottom: '10px' }}
                                    />
                                </div>

                                <Container
                                    css={css`
                                        display: flex;
                                        justify-content: center;
                                        margin-bottom: 10px;
                                    `}
                                >
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading || !email || (step === PASSWORD && !password)}
                                    >
                                        {loading && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                css={css`
                                                    margin-right: 8px;
                                                `}
                                            />
                                        )}
                                        {step === IDENTIFY ? 'Continue' : 'Sign In'}
                                    </Button>
                                </Container>

                                {!!errors.length &&
                                    errors.map((error) => (
                                        <p style={{ color: 'red', fontWeight: '600', textAlign: 'center' }}>{error}</p>
                                    ))}

                                {step === PASSWORD && (
                                    <Container
                                        style={{ textAlign: 'center' }}
                                        className="d-flex justify-content-center"
                                    >
                                        <Link to={ROUTES.PASSWORD_FORGET}>
                                            <b>Forgot password?</b>
                                        </Link>
                                    </Container>
                                )}

                                <Container
                                    style={{ textAlign: 'center' }}
                                    className="d-flex flex-column justify-content-center"
                                >
                                    Don't have an account?
                                    <Link to={ROUTES.SHIPPER_SIGN_UP}>
                                        <b>Shipper Sign Up</b>
                                    </Link>
                                    <Link to={ROUTES.CARRIER_SIGN_UP}>
                                        <b>Carrier Sign Up</b>
                                    </Link>
                                    <Link to={ROUTES.CORPORATE_SIGNUP}>
                                        <b>Corporate Admin Sign Up</b>
                                    </Link>
                                </Container>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <FooterComponent />
        </>
    );
}

export default Auth;
